import type { Updater } from '@tanstack/vue-table';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { Ref } from 'vue';

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export function valueUpdater<T extends Updater<any>>(
    updaterOrValue: T,
    ref: Ref
) {
    ref.value =
        typeof updaterOrValue === 'function'
            ? updaterOrValue(ref.value)
            : updaterOrValue;
}

/**
 * Utility function to manage success/error notifications with automatic timeout
 * @param successRef - Reactive reference for success messages
 * @param errorRef - Reactive reference for error messages
 * @param type - 'success' or 'error'
 * @param message - The message to display
 * @param duration - Duration in milliseconds before auto-clearing (default: 3000)
 */
export function showNotification(
    successRef: Ref<string | null>,
    errorRef: Ref<string | null>,
    type: 'success' | 'error',
    message: string,
    duration: number = 3000
) {
    // Clear both messages first
    successRef.value = null;
    errorRef.value = null;

    // Set the appropriate message
    if (type === 'success') {
        successRef.value = message;
    } else {
        errorRef.value = message;
    }

    // Auto-clear after duration
    setTimeout(() => {
        if (type === 'success') {
            successRef.value = null;
        } else {
            errorRef.value = null;
        }
    }, duration);
}

/**
 * Utility function to clear all notifications
 * @param successRef - Reactive reference for success messages
 * @param errorRef - Reactive reference for error messages
 */
export function clearNotifications(
    successRef: Ref<string | null>,
    errorRef: Ref<string | null>
) {
    successRef.value = null;
    errorRef.value = null;
}
